import React, { useState } from 'react';
import { Box, Button, Center, Divider, Grid, Group, Loader, Stack, Text } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { IconArrowRight, IconCalendar, IconFileText, IconSparkles } from '@tabler/icons-react';
import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PageContainer } from '../../components/ui/PageContainer';
import {
  useGetPerformanceReviewRounds,
  useGetPerformanceReviewRoundsActive,
  useGetPerformanceReviewRoundsRoundIdFormsMine,
  useGetPerformanceReviewRoundsRoundIdFormsAboutMe,
  useGetPerformanceReviewRoundsRoundIdParticipants,
  useGetPerformanceReviewRoundsRoundIdParticipantsMe,
  useGetPerformanceReviewRoundsRoundIdSummarizersMine,
  useGetPerformanceReviewRoundsIdResults,
  useGetPerformanceReviewQuestionnairesId,
} from '../../api/generated/performance-review/performance-review';
import { GetPerformanceReviewRoundsRoundIdFormsAboutMe200Item } from '../../api/generated/model/getPerformanceReviewRoundsRoundIdFormsAboutMe200Item';
import { PerformanceReviewRound } from '../../api/generated/model/performanceReviewRound';
import { PerformanceReviewForm } from '../../api/generated/model/performanceReviewForm';
import { PerformanceReviewQuestionnaire } from '../../api/generated/model/performanceReviewQuestionnaire';
import { useGetUsersMe } from '../../api/generated/users/users';
import { useGrades } from '../../hooks/useReferenceApi';
import { useMyReportIds } from '../../hooks/useTeamApi';
import { usePermission } from '../../hooks/usePermission';
import { useDevice } from '../../hooks/useDevice';
import { User, Grade } from '../../types';
import { PerformanceReviewFormModal } from '../../components/review/PerformanceReviewFormModal';
import type { OutcomeValues } from '../../components/review/PerformanceReviewOutcomePanel';
import { PerformanceReviewSummaryEditModal } from '../../components/review/PerformanceReviewSummaryEditModal';
import { GetPerformanceReviewRoundsRoundIdSummarizersMine200Item as MySummarizerRow } from '../../api/generated/model/getPerformanceReviewRoundsRoundIdSummarizersMine200Item';
import { api } from '../../utils/api';

import { ActionShell } from './parts/ActionShell';
import { DraftHint } from './parts/DraftHint';
import { EssayBody } from './parts/EssayBody';
import { FeedbackBody } from './parts/FeedbackBody';
import { FinishedBody } from './parts/FinishedBody';
import { PublishedSummaryBody } from './parts/PublishedSummaryBody';
import { weightedMean } from './parts/score';
import { ManagedResultsCard } from './parts/ManagedResultsCard';
import { MySummariesCard, MySummaryRow } from './parts/MySummariesCard';
import { HistoryBlock } from './parts/HistoryBlock';
import { NumberMono } from './parts/primitives';
import { RoundHero } from './parts/RoundHero';
import { AboutRound, MeMini, MyReviewers } from './parts/SideRail';
import { SummaryBody } from './parts/SummaryBody';
import {
  computeDeadline,
  computeDelta,
  computeEssayBlocks,
  computeStageWindows,
  computeSummaryBlocks,
  decisionI18nKey,
  formatPeriod,
  relationFromFormType,
  stageOrder,
} from './parts/helpers';
import type {
  CompetencyRow,
  HistoryItem,
  MyReviewerView,
  ParticipantMe,
  PeerStatus,
  PeerView,
  ResultEntry,
  StageDef,
  ViewStage,
} from './parts/types';

export const PerformanceReview: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const device = useDevice();
  const isMobile = device === 'mobile';

  // ── data ──────────────────────────────────────────────────────────────────
  const { data: active, isLoading: activeLoading } = useGetPerformanceReviewRoundsActive({
    query: { refetchInterval: 15_000, staleTime: 0 },
  });
  const { data: rounds } = useGetPerformanceReviewRounds();
  const { data: me } = useGetUsersMe();
  // Нет идущего раунда — показываем итоги последнего закрытого: иначе после стадии
  // «Завершён» раунд пропадал с главной, и итоги находились только в истории.
  const lastClosedRound = ((rounds as unknown as PerformanceReviewRound[] | undefined) || [])
    .filter((r) => r.status === 'closed')
    .sort((a, b) => +new Date(b.endDate ?? 0) - +new Date(a.endDate ?? 0))[0];
  const activeRound =
    (active as unknown as PerformanceReviewRound | undefined) || lastClosedRound || undefined;
  const { data: myForms } = useGetPerformanceReviewRoundsRoundIdFormsMine(activeRound?._id || '', {
    query: { enabled: !!activeRound?._id, refetchInterval: 15_000, staleTime: 0 },
  });
  const { data: participants } = useGetPerformanceReviewRoundsRoundIdParticipants(
    activeRound?._id || '',
    { query: { enabled: !!activeRound?._id } },
  );
  const { data: aboutMe } = useGetPerformanceReviewRoundsRoundIdFormsAboutMe(
    activeRound?._id || '',
    { query: { enabled: !!activeRound?._id, refetchInterval: 15_000, staleTime: 0 } },
  );
  const questionnaireId =
    typeof activeRound?.questionnaire === 'string'
      ? activeRound.questionnaire
      : (activeRound?.questionnaire as { _id?: string } | undefined)?._id || '';
  const { data: questionnaireData } = useGetPerformanceReviewQuestionnairesId(questionnaireId, {
    query: { enabled: !!questionnaireId },
  });
  const { data: myParticipant } = useGetPerformanceReviewRoundsRoundIdParticipantsMe(
    activeRound?._id || '',
    { query: { enabled: !!activeRound?._id, refetchInterval: 15_000, staleTime: 0 } },
  );
  // Not gated on stage=finished any more: a summary is published per-subject, so
  // your result can be ready while the round is still collecting feedback on
  // everyone else. The endpoint only ever returns published summaries, so asking
  // early costs an empty array, not a leak.
  const { data: roundResults } = useGetPerformanceReviewRoundsIdResults(activeRound?._id || '', {
    query: {
      enabled: !!activeRound?._id,
      staleTime: 30_000,
    },
  });
  // `/users` is admin-only — we used to call useUsers() here to build lookup
  // maps, but it 403s for regular users and left the UI showing raw ObjectIds.
  // All names/positions we need on this page now come pre-populated:
  //   - peer forms (myForms[].subject)        — populated by backend
  //   - reviewers about me (aboutMe[].respondent) — populated by backend
  // `/grades` is auth-only (справочник), accessible to everyone.
  // Кого этот человек саммаризирует в текущем раунде. Список личный: сервер
  // фильтрует по `summarizer = я`, посторонний получает пустой массив — так что
  // спрашивать можно у всех, включая тех, кто не отвечает ни за кого.
  const { data: mySummarizerRows } = useGetPerformanceReviewRoundsRoundIdSummarizersMine(
    activeRound?._id || '',
    { query: { enabled: !!activeRound?._id, staleTime: 30_000 } },
  );
  const { data: grades } = useGrades();
  // Свои люди — с сервера по оргграфу, а не поиском лида/PM по командам на фронте.
  const managedUserIds = useMyReportIds();
  // Итоги всей компании (наблюдатель, админ, владелец) — по праву с сервера, не по флагам.
  const seesWholeCompany = usePermission('reviews.results');

  // Кому пишем саммари прямо сейчас — открывает тот же редактор, что и админский
  // экран раунда; право на запись сервер проверяет по назначенному ответственному.
  const [writingSummaryFor, setWritingSummaryFor] = useState<string | null>(null);

  // ── form drawer state (animated open/close) ───────────────────────────────
  const [openFormId, setOpenFormId] = useState<string | null>(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const formClearTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const openForm = (id: string) => {
    if (formClearTimerRef.current) {
      clearTimeout(formClearTimerRef.current);
      formClearTimerRef.current = null;
    }
    setOpenFormId(id);
    setIsFormModalOpen(true);
  };
  const closeForm = () => {
    setIsFormModalOpen(false);
    if (formClearTimerRef.current) clearTimeout(formClearTimerRef.current);
    formClearTimerRef.current = setTimeout(() => setOpenFormId(null), 320);
  };

  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'ru-RU';

  // ── derived data required by hooks ───────────────────────────────────────
  const formsArr = (myForms as unknown as PerformanceReviewForm[]) || [];
  const selfForm = formsArr.find((f) => f.type === 'self');
  const peerForms = formsArr.filter((f) => f.type === 'peer' || f.type === 'manager');
  const allRounds = (rounds as unknown as PerformanceReviewRound[]) || [];
  const closedRounds = allRounds.filter((r) => r.status === 'closed' || r.status === 'archived');
  const historicalSelfFormQueries = useQueries({
    queries: closedRounds.map((r) => ({
      queryKey: ['performance-review', r._id, 'forms-mine-history'],
      queryFn: async () => api(`/performance-review/rounds/${r._id}/forms/mine`),
      enabled: !!r._id,
      staleTime: 30_000,
    })),
  });

  if (activeLoading) {
    return (
      <PageContainer>
        <Center mih="60vh">
          <Loader size="sm" />
        </Center>
      </PageContainer>
    );
  }

  // ── derived ───────────────────────────────────────────────────────────────
  const stage: ViewStage = activeRound?.stage ?? 'no-round';
  const period = formatPeriod(activeRound?.startDate, activeRound?.endDate, locale);
  const meUser = me as User | undefined;
  const meName = meUser?.name || t('perfReview.home.defaultEmployee');
  const mePosition = meUser?.position || '—';
  const meGrade = meUser?.grade?.title || '—';
  const meNextGrade = (() => {
    const currentLevel = meUser?.grade?.level;
    if (currentLevel == null) return '—';
    // Grade ladders are keyed by position: the next grade must be the same
    // position, one level up — otherwise a higher-level grade of a different
    // profession (e.g. "Middle III" for developers) leaks in as the goal.
    const currentPosition = meUser?.grade?.position;
    const list = (grades as Grade[] | undefined) ?? [];
    const sorted = list
      .filter(
        (g) => typeof g.level === 'number' && (!currentPosition || g.position === currentPosition),
      )
      .sort((a, b) => (a.level ?? 0) - (b.level ?? 0));
    const next = sorted.find((g) => (g.level ?? 0) > currentLevel);
    return next?.title || '—';
  })();

  const stageWindows = computeStageWindows(activeRound, t, locale);
  const unscheduled = t('perfReview.home.stageWindow.unscheduled');
  const stages: StageDef[] = stageOrder.map((key) => ({
    key,
    label: t(`perfReview.home.stageLabel.${key}`),
    range: stageWindows[key] ?? unscheduled,
  }));

  const deadline = computeDeadline(activeRound, stage, t, locale);

  const historicalSelfFormByRoundId = new Map<string, string>();
  historicalSelfFormQueries.forEach((query, index) => {
    const roundId = closedRounds[index]?._id;
    const self = ((query.data as PerformanceReviewForm[] | undefined) || []).find(
      (f) => f.type === 'self',
    );
    if (roundId && self?._id) historicalSelfFormByRoundId.set(roundId, self._id);
  });

  const historyItems: HistoryItem[] = closedRounds.map((r) => {
    const p = formatPeriod(r.startDate, r.endDate, locale);
    const roundId = r._id || '';
    return {
      id: roundId,
      title: r.title || '—',
      period: r.startDate || r.endDate ? `${p.start} — ${p.end} ${p.year}` : '',
      closed: r.status === 'closed',
      selfFormId: historicalSelfFormByRoundId.get(roundId),
    };
  });

  const peers: PeerView[] = peerForms.map((f) => {
    // Backend populates subject as { _id, name, avatar, position }; older
    // clients may have it as a bare string id — handle both.
    const subjectObj =
      f.subject && typeof f.subject === 'object'
        ? (f.subject as { _id?: string; name?: string; position?: string; avatar?: string })
        : null;
    const subjectId = subjectObj?._id || (typeof f.subject === 'string' ? f.subject : '');
    const subjectName = subjectObj?.name || subjectId || '—';
    const pos = subjectObj?.position || t('perfReview.home.defaultPosition');
    const avatar = subjectObj?.avatar || undefined;
    const statusMap: Record<string, PeerStatus> = {
      submitted: 'submitted',
      draft: 'draft',
      pending: 'pending',
    };
    const status = statusMap[f.status as string] || 'pending';
    return {
      id: f._id || '',
      name: subjectName,
      position: pos,
      team: '',
      relation: relationFromFormType(f.type),
      status,
      submittedAt: f.submittedAt,
      avatar,
    };
  });

  const participantsList = Array.isArray(participants) ? participants : [];
  const participantsCount = participantsList.length || peerForms.length;

  // Личность ревьюера сюда не приходит намеренно: peer-ревью анонимно для
  // субъекта, ему видно лишь, сколько форм уже сдано.
  const myReviewers: MyReviewerView[] = (
    (aboutMe as GetPerformanceReviewRoundsRoundIdFormsAboutMe200Item[] | undefined) ?? []
  ).map((f) => ({
    status:
      f.status === 'submitted' ? 'submitted' : f.status === 'draft' ? 'in-progress' : 'pending',
  }));

  const roundTitle = activeRound?.title ?? t('perfReview.home.noActiveRoundTitle');
  const roundDescription =
    activeRound?.description ||
    (stage === 'no-round'
      ? t('perfReview.home.fallbackEmptyDescription')
      : t('perfReview.home.fallbackRoundDescription'));
  const questionnaireName =
    (typeof activeRound?.questionnaire === 'object'
      ? (activeRound?.questionnaire as { name?: string } | undefined)?.name
      : undefined) ||
    (questionnaireData as PerformanceReviewQuestionnaire | undefined)?.name ||
    t('perfReview.home.defaultQuestionnaire');
  const managerName = t('perfReview.home.defaultManager');

  const essayBlocks = computeEssayBlocks(
    questionnaireData as PerformanceReviewQuestionnaire | undefined,
    selfForm,
    t,
  );
  const participantMe = (myParticipant ?? null) as ParticipantMe | null;
  const summaryBlocks = computeSummaryBlocks(
    questionnaireData as PerformanceReviewQuestionnaire | undefined,
    participantMe ?? undefined,
  );
  const meeting = (() => {
    if (!participantMe?.meetingAt) {
      return {
        date: '—',
        time: '',
        with: managerName,
      };
    }
    const dt = new Date(participantMe.meetingAt);
    const date = dt.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
    const startTime = dt.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    const durationMin = participantMe.meetingDurationMin ?? 60;
    const endDt = new Date(dt.getTime() + durationMin * 60 * 1000);
    const endTime = endDt.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    return {
      date,
      time: `${startTime} — ${endTime}`,
      with: `${managerName} · 1-on-1`,
    };
  })();

  // Кнопка «В календарь»: .ics-файл, который открывается любым календарём.
  const downloadMeetingIcs = () => {
    if (!participantMe?.meetingAt) return;
    const start = new Date(participantMe.meetingAt);
    const end = new Date(start.getTime() + (participantMe.meetingDurationMin ?? 60) * 60_000);
    const stamp = (d: Date) => d.toISOString().replace(/[-:]|\.\d{3}/g, '');
    const ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Skillscape//Performance Review//EN',
      'BEGIN:VEVENT',
      `UID:pr-1on1-${start.getTime()}@skillscape`,
      `DTSTAMP:${stamp(new Date())}`,
      `DTSTART:${stamp(start)}`,
      `DTEND:${stamp(end)}`,
      `SUMMARY:${meeting.with}`,
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([ics], { type: 'text/calendar' }));
    a.download = 'performance-review-1on1.ics';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const myResultEntry = ((roundResults as unknown as ResultEntry[]) || []).find((r) => {
    const u = typeof r.user === 'object' ? r.user?._id : r.user;
    return u === meUser?._id;
  });
  const finishedCompetencies: CompetencyRow[] = (myResultEntry?.blocks ?? [])
    .filter((b) => typeof b.scoreMean === 'number' && b.block)
    .map((b) => ({ name: b.block!, score: b.scoreMean!, prev: 0 }));
  // Тот же итог, что видит лид на экране результатов: считаем из тех же блоков
  // и той же функцией, иначе под одним заголовком было бы два разных числа.
  const finishedTotal = weightedMean(myResultEntry?.blocks ?? []);
  const finishedHighlights: string[] = participantMe?.highlights ?? [];
  const gradeBeforeTitle =
    (typeof participantMe?.gradeBefore === 'object'
      ? (participantMe.gradeBefore as Grade | null)?.title
      : null) || meGrade;
  const gradeAfterTitle =
    (typeof participantMe?.gradeAfter === 'object'
      ? (participantMe.gradeAfter as Grade | null)?.title
      : null) || meGrade;
  const finishedDelta = computeDelta(participantMe, t);
  const finishedDecisionLabel = participantMe?.decision
    ? t(decisionI18nKey(participantMe.decision))
    : t('perfReview.home.finished.decisionNoChange');

  const isMySummaryPublished = !!participantMe?.summaryPublishedAt;
  // `null` — сервер ответил, что меня нет среди оцениваемых (админ, ревьюер, лид).
  // `undefined` — ответ ещё не пришёл, тогда ничего не утверждаем.
  const isNotSubject = !!activeRound && myParticipant === null;
  const hasOutcome =
    !!participantMe?.decision ||
    (typeof participantMe?.gradeAfter === 'object' && participantMe?.gradeAfter !== null);

  const mySummaries = (
    (mySummarizerRows as unknown as MySummarizerRow[] | undefined) || []
  ).map<MySummaryRow>((r) => ({
    userId: r.user?._id ?? '',
    name: r.user?.name ?? t('perfReview.unknownParticipant'),
    avatar: r.user?.avatar ?? undefined,
    hasDraft: !!r.hasDraft,
    isShared: !!r.summarySharedAt,
    isPublished: !!r.summaryPublishedAt,
    feedbackSubmitted: r.feedback?.submitted ?? 0,
    feedbackTotal: r.feedback?.total ?? 0,
  }));
  const summaryTargetRow = (
    (mySummarizerRows as unknown as MySummarizerRow[] | undefined) || []
  ).find((r) => r.user?._id === writingSummaryFor);

  // /results отдаёт только то, что смотрящему разрешено: своё саммари, тех, за
  // чьи саммари он отвечает, и своих подчинённых как лид или PM. Значит всё,
  // что здесь не я, — это человек с готовым саммари, чей 1-on-1 проводить мне.
  // Исключение — reviews.results на всю компанию: тогда сервер отдаёт всех, и
  // «подопечные» сужаем до своих людей по оргграфу и тех, за чьи саммари человек отвечает.
  const managedResults = ((roundResults as unknown as ResultEntry[]) || [])
    .filter((r) => {
      const u = typeof r.user === 'object' ? r.user?._id : r.user;
      if (!u || u === meUser?._id) return false;
      return (
        !seesWholeCompany || managedUserIds.has(u) || mySummaries.some((row) => row.userId === u)
      );
    })
    .map((r) => {
      const u = typeof r.user === 'object' ? r.user : undefined;
      return {
        userId: u?._id ?? '',
        name: u?.name ?? t('perfReview.unknownParticipant'),
        avatar: u?.avatar,
      };
    });

  // Своя часть работы на текущем этапе закрыта. Раунд при этом может быть
  // просрочен из-за тех, кто ещё не сдал, — но человеку, который сдал всё,
  // красная плашка «просрочено» говорит неправду о нём самом.
  const isMyWorkDone = (() => {
    if (stage === 'collect-essay') return selfForm?.status === 'submitted';
    if (stage === 'collect-feedback')
      return peers.length > 0 && peers.every((p) => p.status === 'submitted');
    // На этапе саммари работа лежит на ответственных: если я не пишу ничего
    // или всё уже опубликовано, ждать от меня нечего.
    if (stage === 'summary') return mySummaries.every((r) => r.isPublished);
    return false;
  })();

  // /results отдаёт только разрешённое смотрящему; пусто — смотреть нечего.
  const hasVisibleResults = ((roundResults as unknown as ResultEntry[]) || []).length > 0;
  const openResultsButton = (
    <Button
      color="teal"
      rightSection={<IconArrowRight size={16} />}
      onClick={() => activeRound && navigate(`/performance-review/${activeRound._id}/results`)}
    >
      {t('perfReview.home.next.openResults')}
    </Button>
  );

  // ── stage-specific action card ───────────────────────────────────────────
  const nextAction = (() => {
    // Оцениваемым не являюсь: эссе, встреча и решение по грейду — не про меня.
    // На этапе обратной связи карточка остаётся: ревьюеру есть что делать.
    if (isNotSubject && stage !== 'collect-feedback' && stage !== 'no-round') {
      return (
        <ActionShell
          color="gray"
          eyebrow={t('perfReview.home.next.notSubjectEyebrow')}
          title={t('perfReview.home.next.notSubjectTitle')}
          description={t('perfReview.home.next.notSubjectDesc')}
          right={hasVisibleResults ? openResultsButton : undefined}
        />
      );
    }
    // Решение по грейду пишет ответственный после 1-on-1; пока его нет, не
    // выдумываем «грейд → грейд, без изменений», но вход в результаты оставляем.
    if (stage === 'finished' && !hasOutcome) {
      return (
        <ActionShell
          color="gray"
          eyebrow={t('perfReview.home.next.finishedEyebrow')}
          title={t('perfReview.home.next.finishedNoDecisionTitle')}
          description={t(
            hasVisibleResults
              ? 'perfReview.home.next.finishedResultsReady'
              : 'perfReview.home.next.finishedNoDecisionDesc',
          )}
          right={hasVisibleResults ? openResultsButton : undefined}
        />
      );
    }
    if (stage === 'collect-essay') {
      const filled = essayBlocks.reduce((a, b) => a + b.filled, 0);
      const total = essayBlocks.reduce((a, b) => a + b.total, 0);
      return (
        <ActionShell
          eyebrow={t('perfReview.home.next.doNow')}
          title={t('perfReview.home.next.essayTitle')}
          description={t('perfReview.home.next.essayDesc')}
          right={
            <Group gap="md">
              <Box style={{ textAlign: 'right' }}>
                <Text size="xs" c="dimmed" mb={2}>
                  {t('perfReview.home.next.progress')}
                </Text>
                <NumberMono style={{ fontSize: 22, fontWeight: 700 }}>
                  {filled}
                  <Text component="span" c="dimmed" fw={500} inherit>
                    /{total}
                  </Text>
                </NumberMono>
              </Box>
              <Button
                variant="light"
                rightSection={<IconArrowRight size={16} />}
                disabled={!selfForm}
                onClick={() => selfForm && navigate(`/performance-review/forms/${selfForm._id}`)}
              >
                {selfForm?.status === 'submitted'
                  ? t('perfReview.home.next.openEssay')
                  : filled === 0
                    ? t('perfReview.home.next.startEssay')
                    : t('perfReview.home.next.continueEssay')}
              </Button>
            </Group>
          }
        />
      );
    }
    if (stage === 'collect-feedback') {
      const done = peers.filter((p) => p.status === 'submitted').length;
      const left = peers.length - done;
      const nextOne = peers.find((p) => p.status !== 'submitted');
      return (
        <ActionShell
          eyebrow={t('perfReview.home.next.doNow')}
          title={
            left === 0
              ? t('perfReview.home.next.feedbackAllDoneTitle')
              : t('perfReview.home.next.feedbackTitle', { count: left })
          }
          description={
            left === 0
              ? t('perfReview.home.next.feedbackAllDoneDesc')
              : t('perfReview.home.next.feedbackDesc')
          }
          right={
            <Group gap="md">
              <Box style={{ textAlign: 'right' }}>
                <Text size="xs" c="dimmed" mb={2}>
                  {t('perfReview.home.next.submitted')}
                </Text>
                <NumberMono style={{ fontSize: 22, fontWeight: 700 }}>
                  {done}
                  <Text component="span" c="dimmed" fw={500} inherit>
                    /{peers.length}
                  </Text>
                </NumberMono>
              </Box>
              {left > 0 && (
                <Button
                  variant="light"
                  rightSection={<IconArrowRight size={16} />}
                  disabled={!nextOne}
                  onClick={() => nextOne && openForm(nextOne.id)}
                >
                  {done === 0
                    ? t('perfReview.home.next.startReview')
                    : t('perfReview.home.next.nextReview')}
                </Button>
              )}
            </Group>
          }
        />
      );
    }
    if (stage === 'summary') {
      return (
        <ActionShell
          color="gray"
          eyebrow={t('perfReview.home.next.happeningNow')}
          title={t('perfReview.home.next.summaryTitle')}
          description={t('perfReview.home.next.summaryDesc')}
          right={
            <Group gap="md">
              <Box style={{ textAlign: 'right' }}>
                <Text size="xs" c="dimmed" mb={2}>
                  {t('perfReview.home.next.meeting')}
                </Text>
                <Text size="md" fw={600}>
                  {meeting.date}
                </Text>
                <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {meeting.time}
                </Text>
              </Box>
              {participantMe?.meetingAt && (
                <Button
                  variant="default"
                  leftSection={<IconCalendar size={14} />}
                  onClick={downloadMeetingIcs}
                >
                  {t('perfReview.home.next.toCalendar')}
                </Button>
              )}
            </Group>
          }
        />
      );
    }
    if (stage === 'finished') {
      return (
        <ActionShell
          eyebrow={
            <Group gap={4} component="span">
              <IconSparkles size={11} />
              <span>{t('perfReview.home.next.finishedEyebrow')}</span>
            </Group>
          }
          title={
            // Без грейдов вышло бы «— → —».
            gradeAfterTitle === '—' ? (
              t('perfReview.home.next.finishedNoDecisionTitle')
            ) : (
              <>
                {gradeBeforeTitle} →{' '}
                <Text component="span" c="teal" inherit>
                  {gradeAfterTitle}
                </Text>
              </>
            )
          }
          description={
            <>
              {t('perfReview.home.next.finishedDescPrefix')}
              <strong>{finishedDecisionLabel}</strong>
              {t('perfReview.home.next.finishedDescSuffix')}
            </>
          }
          right={hasVisibleResults ? openResultsButton : undefined}
        />
      );
    }
    return (
      <ActionShell
        color="gray"
        eyebrow={t('perfReview.home.next.noRoundEyebrow')}
        title={t('perfReview.home.next.noRoundTitle')}
        description={t('perfReview.home.next.noRoundDesc')}
        right={
          historyItems.length > 0 ? (
            <Button
              variant="default"
              rightSection={<IconArrowRight size={14} />}
              onClick={() => navigate(`/performance-review/${historyItems[0].id}/results`)}
            >
              {t('perfReview.home.next.openLastClosed')}
            </Button>
          ) : undefined
        }
      />
    );
  })();

  // ── stage body ────────────────────────────────────────────────────────────
  const stageBody = (() => {
    if (isNotSubject && stage !== 'collect-feedback' && stage !== 'no-round') return null;
    if (stage === 'collect-essay') {
      return (
        <EssayBody
          blocks={essayBlocks}
          isMobile={isMobile}
          onOpen={() => selfForm && navigate(`/performance-review/forms/${selfForm._id}`)}
        />
      );
    }
    if (stage === 'collect-feedback') {
      return <FeedbackBody peers={peers} onOpenForm={(id) => openForm(id)} isMobile={isMobile} />;
    }
    if (stage === 'summary') return <SummaryBody blocks={summaryBlocks} />;
    if (stage === 'finished') {
      if (finishedCompetencies.length === 0 && finishedHighlights.length === 0) return null;
      return (
        <FinishedBody
          competencies={finishedCompetencies}
          highlights={finishedHighlights}
          delta={finishedDelta}
          total={finishedTotal}
        />
      );
    }
    return (
      <DraftHint
        hasHistory={historyItems.length > 0}
        onOpenLast={() =>
          historyItems[0] && navigate(`/performance-review/${historyItems[0].id}/results`)
        }
      />
    );
  })();

  const showReviewersSide =
    stage === 'collect-feedback' || stage === 'summary' || stage === 'finished';

  return (
    <PageContainer>
      <Stack gap="lg">
        <RoundHero
          title={roundTitle}
          description={roundDescription}
          period={period}
          participantsCount={participantsCount}
          stages={stages}
          stage={stage}
          deadline={deadline}
          isMyWorkDone={isMyWorkDone}
          isMobile={isMobile}
        />

        {nextAction}

        <Grid gutter="lg">
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Stack gap="lg">
              {/* Sits ABOVE the stage body rather than replacing it: your summary
                  being published doesn't mean the round is over for you — you may
                  still owe reviews on people who aren't done yet. */}
              {isMySummaryPublished && stage !== 'finished' && (
                <PublishedSummaryBody
                  blocks={participantMe?.summaryBlocks ?? []}
                  scores={myResultEntry?.blocks}
                  comparison={myResultEntry?.comparison}
                  legacyText={participantMe?.summaryPublished}
                  publishedAt={participantMe?.summaryPublishedAt}
                  locale={locale}
                />
              )}
              {mySummaries.length > 0 && (
                <MySummariesCard rows={mySummaries} onWrite={setWritingSummaryFor} />
              )}
              {managedResults.length > 0 && (
                <ManagedResultsCard
                  people={managedResults}
                  onOpen={(userId) =>
                    navigate(`/performance-review/${activeRound?._id}/results?user=${userId}`)
                  }
                />
              )}
              {stageBody}
              <Divider my="xs" variant="dashed" />
              <HistoryBlock
                items={historyItems}
                onOpen={(id) => navigate(`/performance-review/${id}/results`)}
                onOpenEssay={(formId) => navigate(`/performance-review/forms/${formId}`)}
              />
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Stack gap="md" style={isMobile ? undefined : { position: 'sticky', top: 80 }}>
              <MeMini
                name={meName}
                position={mePosition}
                grade={meGrade}
                nextGrade={meNextGrade}
                avatar={meUser?.avatar}
              />
              {stage !== 'no-round' && (
                <AboutRound
                  period={period}
                  participantsCount={participantsCount}
                  questionnaire={questionnaireName}
                />
              )}
              {selfForm?._id && (
                <Button
                  fullWidth
                  variant="light"
                  color="teal"
                  leftSection={<IconFileText size={16} />}
                  onClick={() => navigate(`/performance-review/forms/${selfForm._id}`)}
                >
                  {t('perfReview.home.side.openMyEssay')}
                </Button>
              )}
              {showReviewersSide && myReviewers.length > 0 && <MyReviewers items={myReviewers} />}
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>

      <PerformanceReviewFormModal
        formId={openFormId ?? ''}
        opened={isFormModalOpen}
        onClose={closeForm}
      />

      {writingSummaryFor && activeRound?._id && (
        <PerformanceReviewSummaryEditModal
          opened={!!writingSummaryFor}
          onClose={() => setWritingSummaryFor(null)}
          roundId={activeRound._id}
          userId={writingSummaryFor}
          // Схема свагера метит поля блока необязательными; редактор берёт их
          // строками, поэтому подставляем пустые.
          initialBlocks={(summaryTargetRow?.summaryBlocks ?? []).map((b) => ({
            block: b.block ?? '',
            comment: b.comment ?? '',
          }))}
          isPublished={!!summaryTargetRow?.summaryPublishedAt}
          isShared={!!summaryTargetRow?.summarySharedAt}
          // Сгенерированная схема ещё не знает полей итога — бэкенд их отдаёт.
          initialOutcome={summaryTargetRow as OutcomeValues | undefined}
        />
      )}
    </PageContainer>
  );
};
